import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAccounts, type Account } from "@/api/accounts";
import { useCategories, pickerOptions, type Category, type CategoryKind } from "@/api/categories";
import {
  useCreateRule,
  useDeleteRule,
  useOccurrenceAction,
  useRecurringRules,
  useSetRuleActive,
  useUpcoming,
  useUpdateRule,
  type Occurrence,
  type RecurringRule,
} from "@/api/recurring";
import { Amount } from "@/components/Amount";
import { AmountError } from "@/money";
import {
  Collapsible,
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  PageTitle,
  Panel,
  Segmented,
  Tag,
  buttonClass,
  inputClass,
  quietButtonClass,
  rowsClass,
  secondaryButtonClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import type { MessageKey } from "@/i18n/catalog";
import { dayOfMonth, isoWeekday, today } from "@/dates";
import { recurringTotals, type RuleLine } from "@/recurringTotals";

/**
 * How far ahead the list looks, in months. The server materialises twelve
 * (K-23), so twelve is the most there is to show; three is the default because
 * it is what somebody planning this month and the next actually reads.
 */
const HORIZONS = [3, 6, 12] as const;
type Horizon = (typeof HORIZONS)[number];

function horizonOf(raw: string | null): Horizon {
  const parsed = Number(raw);
  return HORIZONS.find((h) => h === parsed) ?? 3;
}

/**
 * What is coming up, and what to do about it (`model.md` §3.7).
 *
 * Every row is an occurrence — a materialised row rather than a computed one —
 * and the two buttons are the reason it has to be. "Skip this month's gym
 * payment" and "this one has been paid" are **state**: no schedule can express
 * either, and the next regeneration has to leave the answer alone. K-23's
 * planner and the store's own delete predicate both see to that; this screen is
 * where somebody says it.
 *
 * Skipped rows stay in the list, struck through. A view that hid them would
 * leave somebody unable to change their mind, and changing your mind about a
 * month you are not paying for should be as cheap as deciding it.
 *
 * Below the occurrences are the rules that produce them. Changing a rule
 * regenerates its forecasts and leaves decided months alone; pausing is offered
 * beside deleting because a membership suspended for the winter is not a
 * mistake to be erased. The horizon lives in the URL, like the report's month.
 *
 * Every part starts folded away — the occurrences, the rules, and what the
 * rules add up to per month and per year, rule by rule and by category — so
 * the page opens as a short index and each part is opened on purpose.
 */
export function UpcomingPage() {
  const t = useT();
  const { householdId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const horizon = horizonOf(params.get("months"));

  const rules = useRecurringRules(householdId);
  const upcoming = useUpcoming(householdId, horizon);
  const accounts = useAccounts(householdId);
  const categories = useCategories(householdId);
  // The sums name a rule's category even after it is archived; the picker must
  // not offer one, so the two lists are fetched apart.
  const allCategories = useCategories(householdId, true);
  const act = useOccurrenceAction(householdId);

  const [editing, setEditing] = useState<RecurringRule>();

  const accountList = accounts.data?.data ?? [];
  const ruleList = rules.data?.items ?? [];
  const ruleOf = (id: string) => ruleList.find((rule) => rule.id === id);
  const fallbackCurrency = accountList[0]?.currency_code ?? "EUR";

  function chooseHorizon(next: Horizon) {
    const updated = new URLSearchParams(params);
    if (next === 3) updated.delete("months");
    else updated.set("months", String(next));
    setParams(updated, { replace: true });
  }

  // The list reaches back for forecasts nobody dealt with; what is already
  // booked or skipped and in the past is history, and stays out of it.
  const todayIso = today();
  const rows = (upcoming.data?.items ?? []).filter(
    (occurrence) => occurrence.due_date >= todayIso || occurrence.status === "forecast",
  );

  return (
    <div>
      <PageTitle>{t("nav.upcoming")}</PageTitle>

      {/* Every part starts folded; the page opens as an index of three. */}
      <Collapsible
        title={t("upcoming.bookings")}
        meta={upcoming.isLoading ? undefined : rows.length}
        testId="upcoming-bookings"
      >
        <div
          role="group"
          aria-label={t("upcoming.horizon")}
          className="flex flex-wrap items-center gap-3"
        >
          <span className="text-sm text-muted">{t("upcoming.horizon")}</span>
          <div className="flex rounded-control border border-line bg-sunken p-1">
            {HORIZONS.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={horizon === value}
                onClick={() => chooseHorizon(value)}
                className={`min-h-10 rounded-[0.375rem] px-3 text-sm transition-colors ${
                  horizon === value
                    ? "bg-surface font-medium text-ink shadow-sm ring-1 ring-line"
                    : "text-muted hover:text-ink"
                }`}
              >
                {t("upcoming.months", { count: value })}
              </button>
            ))}
          </div>
        </div>

        {upcoming.isLoading && <Loading />}
        <ErrorNote error={upcoming.error} />
        <ErrorNote error={act.error} />

        {!upcoming.isLoading && rows.length === 0 && (
          <EmptyState title={t("upcoming.emptyTitle")}>{t("upcoming.emptyBody")}</EmptyState>
        )}

        {rows.length > 0 && (
          <ul className={`mt-6 ${rowsClass}`}>
            {rows.map((occurrence) => (
              <li key={occurrence.id} className="py-3" data-testid="occurrence">
                <OccurrenceRow
                  occurrence={occurrence}
                  name={ruleOf(occurrence.recurring_rule_id)?.name ?? t("upcoming.rule")}
                  // Each rule is denominated in its own account's currency, which
                  // need not be the household's first account's.
                  currency={
                    ruleOf(occurrence.recurring_rule_id)?.template_currency_code ??
                    fallbackCurrency
                  }
                  pending={act.isPending}
                  onAct={(action) => act.mutate({ id: occurrence.id, action })}
                />
              </li>
            ))}
          </ul>
        )}
      </Collapsible>

      {ruleList.length > 0 && (
        <Collapsible title={t("upcoming.rules")} meta={ruleList.length} testId="upcoming-rules">
          <ul className={rowsClass}>
            {ruleList.map((rule) => (
              <li key={rule.id} className="py-3" data-testid="rule">
                <RuleRow
                  householdId={householdId}
                  rule={rule}
                  editing={editing?.id === rule.id}
                  onEdit={() => setEditing(rule)}
                  onDeleted={() => {
                    if (editing?.id === rule.id) setEditing(undefined);
                  }}
                />
              </li>
            ))}
          </ul>
        </Collapsible>
      )}

      {ruleList.length > 0 && (
        <RecurringSums rules={ruleList} categories={allCategories.data?.items ?? []} />
      )}

      <RuleForm
        // A fresh form per rule, so switching from one rule to another cannot
        // carry the first one's half-typed values into the second.
        key={editing?.id ?? "new"}
        householdId={householdId}
        accounts={accountList}
        categories={categories.data?.items ?? []}
        rule={editing}
        onDone={() => setEditing(undefined)}
      />
    </div>
  );
}

/**
 * What the running rules add up to: three figures, then the rules behind them,
 * then the outgoing again by category. The arithmetic is `recurringTotals`'s;
 * this only lays it out.
 */
function RecurringSums({
  rules,
  categories,
}: {
  rules: RecurringRule[];
  categories: Category[];
}) {
  const t = useT();
  const format = useFormat();
  // Rules the reader has unticked to see what the page would look like without
  // them. Local to this screen and gone on reload: it is a what-if, not a
  // decision, and nothing is written anywhere.
  const [dropped, setDropped] = useState<ReadonlySet<string>>(new Set());

  const now = today();
  const all = useMemo(() => recurringTotals(rules, categories, now), [rules, categories, now]);
  // Dropping only ever removes outgoing, so the same function answers for what
  // is left: one summation, not a second one that could disagree with the first.
  const kept = useMemo(
    () => recurringTotals(rules.filter((rule) => !dropped.has(rule.id)), categories, now),
    [rules, categories, now, dropped],
  );
  // Currencies come from the full set, so unticking the last payment leaves
  // zeroes on screen rather than taking the totals away with it.
  const rowsOf = all.totals.map(
    (entry) =>
      kept.totals.find((k) => k.currency === entry.currency) ?? {
        currency: entry.currency,
        monthlyIncome: 0,
        monthlyOutgoing: 0,
        yearlyOutgoing: 0,
      },
  );

  function toggle(id: string) {
    setDropped((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // A label says its currency only when there is more than one to tell apart.
  const label = (key: MessageKey, currency: string) =>
    rowsOf.length > 1 ? `${t(key)} (${currency})` : t(key);

  const savedOf = (currency: string) => {
    const before = all.totals.find((entry) => entry.currency === currency);
    const after = kept.totals.find((entry) => entry.currency === currency);
    return {
      monthly: (after?.monthlyOutgoing ?? 0) - (before?.monthlyOutgoing ?? 0),
      yearly: (after?.yearlyOutgoing ?? 0) - (before?.yearlyOutgoing ?? 0),
    };
  };

  return (
    <Collapsible title={t("upcoming.totals")} testId="upcoming-totals">
      <p className="text-sm text-muted">{t("upcoming.totalsHint")}</p>

      {rowsOf.length > 0 && (
        <dl className="mt-4 max-w-md" data-testid="recurring-totals">
          {rowsOf.map((entry) => {
            const saved = savedOf(entry.currency);
            return (
              <div key={entry.currency}>
                <SumRow label={label("upcoming.monthlyIncome", entry.currency)} testId="monthly-income">
                  <Amount minor={entry.monthlyIncome} currency={entry.currency} />
                </SumRow>
                <SumRow label={label("upcoming.monthlyOutgoing", entry.currency)} testId="monthly-outgoing">
                  <Amount minor={entry.monthlyOutgoing} currency={entry.currency} />
                </SumRow>
                <SumRow label={label("upcoming.yearlyOutgoing", entry.currency)} testId="yearly-outgoing">
                  <Amount minor={entry.yearlyOutgoing} currency={entry.currency} />
                </SumRow>
                {/* Always laid out, always the same height, hidden until
                    something is unticked: a line that appeared on the first
                    tick pushed the whole list down under the pointer. Two
                    lines tall, so it also holds on a phone. */}
                <div
                  className={`mt-2 flex h-14 items-center rounded-control px-3 text-sm ${
                    saved.yearly !== 0 ? "bg-accent-soft text-accent-text" : "invisible"
                  }`}
                  aria-hidden={saved.yearly === 0}
                  data-testid="saved"
                >
                  {saved.yearly !== 0 &&
                    t("upcoming.saved", {
                      monthly: format.amount(saved.monthly, entry.currency),
                      yearly: format.amount(saved.yearly, entry.currency),
                    })}
                </div>
              </div>
            );
          })}
        </dl>
      )}

      {all.income.length > 0 && (
        <SumTable
          title={t("upcoming.income")}
          testId="income-rules"
          rows={all.income.map((line) => lineRow(line))}
        />
      )}
      {all.outgoing.length > 0 && (
        <SumTable
          title={t("upcoming.outgoing")}
          testId="outgoing-rules"
          rows={all.outgoing.map((line) => lineRow(line))}
          check={{
            hint: t("upcoming.checkHint"),
            isOn: (key) => !dropped.has(key),
            onToggle: toggle,
            label: (name) => t("upcoming.include", { name }),
            reset: dropped.size > 0 ? () => setDropped(new Set()) : undefined,
          }}
        />
      )}
      {kept.byCategory.length > 0 && (
        <SumTable
          title={t("upcoming.byCategory")}
          testId="outgoing-by-category"
          rows={kept.byCategory.map((line) => ({
            key: `${line.categoryId ?? ""}|${line.currency}`,
            name: line.name ?? t("report.uncategorised"),
            currency: line.currency,
            monthly: line.monthly,
            yearly: line.yearly,
          }))}
        />
      )}
    </Collapsible>
  );
}

function lineRow(line: RuleLine) {
  return {
    key: line.rule.id,
    name: line.rule.name,
    currency: line.rule.template_currency_code,
    monthly: line.monthly,
    yearly: line.yearly,
  };
}

function SumRow({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
      <dt className="text-muted">{label}</dt>
      <dd className="font-medium" data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

/** A name and its figure per month and per year, one row each. */
function SumTable({
  title,
  testId,
  rows,
  check,
}: {
  title: string;
  testId: string;
  rows: Array<{ key: string; name: string; currency: string; monthly: number; yearly: number }>;
  /** Puts a tick box on every row; an unticked row is shown, but muted. */
  check?: {
    hint: string;
    isOn: (key: string) => boolean;
    onToggle: (key: string) => void;
    label: (name: string) => string;
    reset: (() => void) | undefined;
  };
}) {
  const t = useT();
  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-sm font-semibold text-ink">{title}</h3>
        {/* Always laid out, hidden until there is something to put back, so
            the row keeps the height of the button and the table below stays
            where it is when the first payment is unticked. */}
        {check && (
          <button
            type="button"
            onClick={check.reset}
            disabled={!check.reset}
            tabIndex={check.reset ? 0 : -1}
            aria-hidden={!check.reset}
            className={`${quietButtonClass} -mr-2 ${check.reset ? "" : "invisible"}`}
          >
            {t("upcoming.includeAll")}
          </button>
        )}
      </div>
      {check && <p className="mt-0.5 text-xs text-muted">{check.hint}</p>}
      <table className="mt-2 w-full text-sm" data-testid={testId}>
        <thead className="text-xs font-medium text-muted">
          <tr className="border-b border-line">
            {check && (
              <th className="w-10 py-2 pr-1">
                <span className="sr-only">{t("upcoming.counts")}</span>
              </th>
            )}
            <th className="py-2 pr-3 text-left">{t("account.name")}</th>
            <th className="py-2 pr-3 text-right">{t("upcoming.perMonth")}</th>
            <th className="py-2 text-right">{t("upcoming.perYear")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {rows.map((row) => {
            const on = check ? check.isOn(row.key) : true;
            return (
              <tr key={row.key} className={on ? "" : "text-muted"} data-counted={on}>
                {check && (
                  <td className="py-0.5 pr-1">
                    {/* The whole 44px cell is the target, not the 16px box. */}
                    <label className="flex min-h-11 w-10 cursor-pointer items-center justify-center">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => check.onToggle(row.key)}
                        aria-label={check.label(row.name)}
                        className="h-4 w-4 cursor-pointer accent-[rgb(var(--accent))]"
                      />
                    </label>
                  </td>
                )}
                <td className={`py-2.5 pr-3 ${on ? "text-ink" : "line-through"}`}>{row.name}</td>
                <td className="py-2.5 pr-3 text-right whitespace-nowrap">
                  <Amount
                    minor={row.monthly}
                    currency={row.currency}
                    testId="per-month"
                    className={on ? "" : "line-through"}
                  />
                </td>
                <td className="py-2.5 text-right whitespace-nowrap">
                  <Amount
                    minor={row.yearly}
                    currency={row.currency}
                    testId="per-year"
                    className={on ? "" : "line-through"}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RuleRow({
  householdId,
  rule,
  editing,
  onEdit,
  onDeleted,
}: {
  householdId: string;
  rule: RecurringRule;
  editing: boolean;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const t = useT();
  const format = useFormat();
  const setActive = useSetRuleActive(householdId);
  const remove = useDeleteRule(householdId);
  const [confirming, setConfirming] = useState(false);

  const details = [
    describeSchedule(rule, t, format),
    rule.end_date
      ? t("upcoming.until", { date: format.date(rule.end_date) })
      : t("upcoming.noEnd"),
  ];
  if (rule.auto_book) details.push(t("upcoming.autoBookTag"));
  if (rule.is_active && rule.next_due_date) {
    details.push(t("upcoming.next", { date: format.date(rule.next_due_date) }));
  }

  return (
    // The amount stays beside the name it belongs to; the actions get a row of
    // their own on a phone, three equal buttons, rather than wrapping unevenly.
    <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
      <div className="flex items-baseline justify-between gap-3">
        <div className={rule.is_active ? "" : "text-muted"}>
          <p className="flex items-center gap-2 text-sm text-ink">
            {rule.name}
            {!rule.is_active && <Tag>{t("upcoming.paused")}</Tag>}
          </p>
          <p className="text-xs text-muted" data-testid="rule-details">
            {details.join(" · ")}
          </p>
        </div>
        <Amount
          minor={rule.template_amount_minor}
          currency={rule.template_currency_code}
          className={`shrink-0 ${rule.is_active ? "" : "text-muted"}`}
        />
      </div>

      <div className="grid grid-cols-3 items-center gap-2 sm:flex">
        {confirming ? (
          <>
            <span className="text-xs text-muted">{t("upcoming.deleteQuestion")}</span>
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate(rule.id, { onSuccess: onDeleted })}
              className={`${buttonClass} min-h-11 px-3 text-xs`}
            >
              {t("upcoming.deleteConfirm")}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className={`${secondaryButtonClass} min-h-11 px-3 text-xs`}
            >
              {t("common.cancel")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={editing}
              onClick={onEdit}
              className={`${secondaryButtonClass} min-h-11 px-3 text-xs`}
            >
              {t("upcoming.edit")}
            </button>
            <button
              type="button"
              disabled={setActive.isPending}
              onClick={() => setActive.mutate({ id: rule.id, active: !rule.is_active })}
              className={`${secondaryButtonClass} min-h-11 px-3 text-xs`}
            >
              {rule.is_active ? t("upcoming.pause") : t("upcoming.resume")}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(true)}
              className={`${secondaryButtonClass} min-h-11 px-3 text-xs`}
            >
              {t("upcoming.delete")}
            </button>
          </>
        )}
      </div>
      {confirming && (
        <p className="text-xs text-muted sm:col-span-2">{t("upcoming.deleteHint")}</p>
      )}
      <ErrorNote error={setActive.error} />
      <ErrorNote error={remove.error} />
    </div>
  );
}

type Unit = RecurringRule["interval_unit"];
const UNITS: Unit[] = ["day", "week", "month", "year"];

/**
 * A rule's rhythm in words: "monatlich am 28.", "alle 2 Wochen am Freitag",
 * "jährlich am 15.03.".
 *
 * A yearly rule's month is the start date's: the schedule anchors the day in
 * the month it starts, and every step after that adds a whole year.
 */
function describeSchedule(
  rule: RecurringRule,
  t: ReturnType<typeof useT>,
  format: ReturnType<typeof useFormat>,
): string {
  const count = rule.interval_count;
  const every = count === 1 ? "one" : "many";
  const anchor = rule.anchor_day ?? 1;
  switch (rule.interval_unit) {
    case "day":
      return t(`upcoming.every.day.${every}`, { count });
    case "week":
      return t(`upcoming.every.week.${every}`, { count, weekday: format.weekday(anchor) });
    case "month":
      return t(`upcoming.every.month.${every}`, { count, day: anchor });
    case "year": {
      const month = Number(rule.start_date.split("-")[1]);
      return t(`upcoming.every.year.${every}`, { count, date: format.dayMonth(anchor, month) });
    }
  }
}

/** A label+input pair without its own top margin, for a shared row (see below). */
function RowField({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
      {error && (
        <p className="mt-1.5 text-xs font-medium text-overspend" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Creates a rule, or changes the one passed in.
 *
 * The amount is a magnitude and the direction supplies the sign, as on the entry
 * form (`model.md` §2.2 rule 1). A salary and a gym membership are both rules.
 *
 * The schedule is the server's (`domain.Schedule`), described rather than
 * recomputed: every so many days, weeks, months or years from a start date, on
 * an anchor — a weekday for a weekly rule, a day of the month for a monthly or
 * yearly one, none for a daily one. The first payment is the first anchor day on
 * or after the start, which is why picking a start date also moves the anchors
 * to it: "starts 15.03.2027, yearly" means every 15 March, not whatever day the
 * form happened to be showing.
 *
 * Editing sends the whole rule back, because a PUT replaces it: the fields this
 * form does not show — the payee, the tolerance, the business flag — are
 * carried over from the rule rather than silently cleared. The account cannot
 * change: it decides the currency, and the server ignores it.
 */
function RuleForm({
  householdId,
  accounts,
  categories,
  rule,
  onDone,
}: {
  householdId: string;
  accounts: Account[];
  categories: Category[];
  rule: RecurringRule | undefined;
  onDone: () => void;
}) {
  const t = useT();
  const format = useFormat();
  const create = useCreateRule(householdId);
  const update = useUpdateRule(householdId);
  const mutation = rule ? update : create;

  const [direction, setDirection] = useState<CategoryKind>(
    rule && rule.template_amount_minor > 0 ? "income" : "expense",
  );
  const [accountId, setAccountId] = useState(rule?.account_id ?? "");
  const [name, setName] = useState(rule?.name ?? "");
  const [amount, setAmount] = useState(() =>
    rule ? format.plain(Math.abs(rule.template_amount_minor), rule.template_currency_code) : "",
  );
  const [unit, setUnit] = useState<Unit>(rule?.interval_unit ?? "month");
  const [count, setCount] = useState(String(rule?.interval_count ?? 1));
  const [startDate, setStartDate] = useState(rule?.start_date ?? today());
  // Two anchors, kept apart, because they mean different things: switching a
  // rule from monthly to weekly and back must not turn "the 28th" into a
  // weekday that does not exist.
  const [weekday, setWeekday] = useState(
    String(rule?.interval_unit === "week" && rule.anchor_day ? rule.anchor_day : isoWeekday(startDate)),
  );
  const [anchorDay, setAnchorDay] = useState(
    String(
      (rule?.interval_unit === "month" || rule?.interval_unit === "year") && rule.anchor_day
        ? rule.anchor_day
        : dayOfMonth(startDate),
    ),
  );
  const [countError, setCountError] = useState<string>();
  const [endDate, setEndDate] = useState(rule?.end_date ?? "");
  const [categoryId, setCategoryId] = useState(rule?.template_category_id ?? "");
  const [autoBook, setAutoBook] = useState(rule?.auto_book ?? false);
  const [amountError, setAmountError] = useState<string>();

  const account = accounts.find((a) => a.id === (accountId || accounts[0]?.id));
  const currency = rule?.template_currency_code ?? account?.currency_code ?? "EUR";

  // Only categories of the chosen kind are offered: a salary filed under
  // "Lebensmittel" would be counted as a refund of groceries by every report
  // that sorts by the category's kind rather than the number's sign.
  const categoryOptions = pickerOptions(
    categories.filter((category) => category.kind === direction),
  );

  function chooseStart(next: string) {
    setStartDate(next);
    if (/^\d{4}-\d{2}-\d{2}$/.test(next)) {
      setWeekday(String(isoWeekday(next)));
      setAnchorDay(String(dayOfMonth(next)));
    }
  }

  function anchorFor(chosen: Unit): { anchor_day?: number } {
    switch (chosen) {
      case "day":
        return {};
      case "week":
        return { anchor_day: Number(weekday) };
      default:
        return { anchor_day: Number(anchorDay) };
    }
  }

  const plural = count === "1" ? "one" : "many";

  const violation = violationsOf(t, mutation.error);

  function chooseDirection(next: CategoryKind) {
    setDirection(next);
    setCategoryId("");
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setAmountError(undefined);
    setCountError(undefined);

    // A whole number of at least one; "every 1.5 months" and "every 0 weeks"
    // are not schedules (the server refuses the second as a loop).
    if (!/^\d+$/.test(count.trim()) || Number(count) < 1) {
      setCountError(t("upcoming.countInvalid"));
      return;
    }

    if (!rule && !account) {
      setAmountError(t("upcoming.needAnAccount"));
      return;
    }
    let minor: number;
    try {
      // The typed number is a magnitude; the direction supplies the sign, for
      // the same reason the entry form does it (`model.md` §2.2 rule 1).
      const magnitude = Math.abs(format.parse(amount, currency));
      minor = direction === "expense" ? -magnitude : magnitude;
    } catch (err) {
      setAmountError(err instanceof AmountError ? t(err.key, err.params) : String(err));
      return;
    }
    if (minor === 0) {
      setAmountError(t("transaction.amountNotZero"));
      return;
    }

    const common = {
      name,
      interval_unit: unit,
      interval_count: Number(count),
      ...anchorFor(unit),
      start_date: startDate,
      ...(endDate ? { end_date: endDate } : {}),
      template_amount_minor: minor,
      ...(categoryId ? { template_category_id: categoryId } : {}),
      auto_book: autoBook,
    };

    if (rule) {
      update.mutate(
        {
          id: rule.id,
          input: {
            ...common,
            ...(rule.payee_id ? { payee_id: rule.payee_id } : {}),
            template_is_business: rule.template_is_business,
            amount_tolerance_bp: rule.amount_tolerance_bp,
          },
        },
        { onSuccess: onDone },
      );
      return;
    }
    if (!account) return;

    create.mutate(
      {
        ...common,
        account_id: account.id,
      },
      {
        onSuccess: () => {
          setName("");
          setAmount("");
          setEndDate("");
        },
      },
    );
  }

  return (
    <Panel as="section" className="mt-8">
      <form onSubmit={onSubmit} noValidate data-testid="rule-form">
        <p className="text-sm font-medium text-ink">
          {rule ? t("upcoming.editing", { name: rule.name }) : t("upcoming.add")}
        </p>

        <div className="mt-4">
          <Segmented
            legend={t("transaction.direction")}
            name="rule-direction"
            value={direction}
            onChange={chooseDirection}
            options={[
              { value: "expense", label: t("categories.expense") },
              { value: "income", label: t("categories.income") },
            ]}
          />
        </div>

        <Field label={t("account.name")} htmlFor="rule-name" error={violation("name")}>
          <input
            id="rule-name"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              direction === "income"
                ? t("upcoming.incomePlaceholder")
                : t("upcoming.namePlaceholder")
            }
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          />
        </Field>

        <Field
          label={t("transactions.account")}
          htmlFor="rule-account"
          {...(rule ? { hint: t("upcoming.accountFixed") } : {})}
          error={violation("account_id")}
        >
          <select
            id="rule-account"
            name="account_id"
            value={rule?.account_id ?? account?.id ?? ""}
            disabled={Boolean(rule)}
            onChange={(e) => setAccountId(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          >
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.currency_code})
              </option>
            ))}
          </select>
        </Field>

        <Field
          label={`${t("transactions.amount")} (${currency})`}
          htmlFor="rule-amount"
          hint={t("upcoming.amountHint")}
          error={amountError ?? violation("template_amount_minor")}
        >
          <input
            id="rule-amount"
            name="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            placeholder={format.plain(2_990, currency)}
            className={`${inputClass} num min-h-11 text-base sm:text-sm`}
          />
        </Field>

        <Field
          label={t("upcoming.repeatsEvery")}
          htmlFor="rule-count"
          error={countError ?? violation("interval_count") ?? violation("interval_unit")}
        >
          {/* A grid, not flex: the shared input style is full-width, which in
              a flex row squeezed the unit picker to nothing. */}
          <div className="grid max-w-sm grid-cols-[5.5rem_1fr] gap-2">
            <input
              id="rule-count"
              name="interval_count"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className={`${inputClass} min-h-11 text-base tabular-nums sm:text-sm`}
            />
            <select
              id="rule-unit"
              name="interval_unit"
              aria-label={t("upcoming.unit")}
              value={unit}
              onChange={(e) => setUnit(e.target.value as Unit)}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            >
              {UNITS.map((value) => (
                <option key={value} value={value}>
                  {t(`upcoming.unit.${value}.${plural}`)}
                </option>
              ))}
            </select>
          </div>
        </Field>

        {/* Start date and its anchor answer one question — "when, exactly?" —
            so they sit in one row once there is room for both. */}
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <RowField
            label={t("upcoming.startDate")}
            htmlFor="rule-start"
            hint={t("upcoming.startDateHint")}
            error={violation("start_date")}
          >
            <input
              id="rule-start"
              name="start_date"
              type="date"
              value={startDate}
              onChange={(e) => chooseStart(e.target.value)}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            />
          </RowField>

          {unit === "week" && (
            <RowField
              label={t("upcoming.weekday")}
              htmlFor="rule-weekday"
              error={violation("anchor_day")}
            >
              <select
                id="rule-weekday"
                name="weekday"
                value={weekday}
                onChange={(e) => setWeekday(e.target.value)}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <option key={day} value={day}>
                    {format.weekday(day)}
                  </option>
                ))}
              </select>
            </RowField>
          )}

          {(unit === "month" || unit === "year") && (
            <RowField
              label={t("upcoming.dayOfMonth")}
              htmlFor="rule-anchor"
              hint={unit === "year" ? t("upcoming.yearHint") : t("upcoming.anchorHint")}
              error={violation("anchor_day")}
            >
              <select
                id="rule-anchor"
                name="anchor_day"
                value={anchorDay}
                onChange={(e) => setAnchorDay(e.target.value)}
                className={`${inputClass} min-h-11 text-base sm:text-sm`}
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    {day}.
                  </option>
                ))}
              </select>
            </RowField>
          )}
        </div>

        <Field
          label={`${t("upcoming.endDate")} (${t("common.optional")})`}
          htmlFor="rule-end"
          hint={t("upcoming.endDateHint")}
          error={violation("end_date")}
        >
          <input
            id="rule-end"
            name="end_date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          />
        </Field>

        <Field
          label={`${t("transaction.category")} (${t("common.optional")})`}
          htmlFor="rule-category"
        >
          <select
            id="rule-category"
            name="template_category_id"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className={`${inputClass} min-h-11 text-base sm:text-sm`}
          >
            <option value="">{t("transaction.withoutCategory")}</option>
            {categoryOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <label className="mt-5 flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            name="auto_book"
            checked={autoBook}
            onChange={(e) => setAutoBook(e.target.checked)}
            className="mt-0.5 h-4 w-4 cursor-pointer accent-[rgb(var(--accent))]"
          />
          <span>
            <span className="block text-sm font-medium text-ink">{t("upcoming.autoBook")}</span>
            <span className="mt-1 block text-xs text-muted">{t("upcoming.autoBookHint")}</span>
          </span>
        </label>

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className={`${buttonClass} min-h-11`}
          >
            {rule
              ? mutation.isPending
                ? t("common.saving")
                : t("common.save")
              : mutation.isPending
                ? t("onboarding.creating")
                : t("common.create")}
          </button>
          {rule && (
            <button
              type="button"
              onClick={onDone}
              className={`${secondaryButtonClass} min-h-11`}
            >
              {t("common.cancel")}
            </button>
          )}
        </div>
        <ErrorNote error={mutation.error} />
      </form>
    </Panel>
  );
}

function OccurrenceRow({
  occurrence,
  name,
  currency,
  pending,
  onAct,
}: {
  occurrence: Occurrence;
  name: string;
  currency: string;
  pending: boolean;
  onAct: (action: "skip" | "unskip" | "confirm") => void;
}) {
  const t = useT();
  const format = useFormat();
  const skipped = occurrence.status === "skipped";
  const confirmed = occurrence.status === "confirmed";
  // A forecast whose day has passed and which nobody has booked or skipped.
  const overdue = occurrence.status === "forecast" && occurrence.due_date < today();

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className={skipped ? "text-muted line-through" : ""}>
        <p className="text-xs text-muted" data-testid="occurrence-due">
          {format.date(occurrence.due_date)}
          {confirmed &&
            (occurrence.match_method === "import_auto"
              ? // K-33: this month was settled by a payment an import
                // recognised, not by somebody pressing the button. Saying which
                // is the difference between "Kontora noticed" and "you told it".
                ` · ${t("upcoming.fromStatement")}`
              : occurrence.match_method === "auto_booked"
                ? // The scheduled job booked it because the rule asked it to.
                  ` · ${t("upcoming.autoBooked")}`
                : ` · ${t("upcoming.booked")}`)}
          {skipped && ` · ${t("upcoming.skipped")}`}
          {overdue && ` · ${t("upcoming.overdue")}`}
        </p>
        <p className="text-sm text-ink">{name}</p>
      </div>

      <div className="flex items-center gap-3">
        <Amount
          minor={occurrence.expected_amount_minor}
          currency={currency}
          className={skipped ? "text-muted line-through" : ""}
          testId="occurrence-amount"
        />
        {!confirmed && (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={pending}
              onClick={() => onAct(skipped ? "unskip" : "skip")}
              className={`${secondaryButtonClass} min-h-11 px-3 text-xs`}
            >
              {skipped ? t("upcoming.unskip") : t("upcoming.skip")}
            </button>
            {!skipped && (
              <button
                type="button"
                disabled={pending}
                onClick={() => onAct("confirm")}
                className={`${buttonClass} min-h-11 px-3 text-xs`}
              >
                {t("upcoming.confirm")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
